import RejectScanButton from "components/RejectScanButton/RejectScanButton";
import RestoreScanButton from "components/RestoreScanButton/RestoreScanButton";
import { OrderContext } from "context/OrderContext";
import { Role } from "models/Role";
import React from "react";
import { Button } from "semantic-ui-react";
import { useAuth } from "services";
import { checkFeatureAuthorization, Features } from "utils";
import { OrderStatus } from "../../models";
import { useT } from "i18n";

export interface OrderModalCommonActionsProps {
  nextStep: () => void;
  cancelOrder: () => void;
  submitOrder: () => void;
  previousStep: () => void;
  currentStep: number;
  initialStep: number;
  isLastStep: boolean;
  finalStep: number;
  currentOrder?: {
    status: OrderStatus;
  };
}

export interface OrderModalActionsProps {
  nextDisabled: boolean;
  archived?: boolean;
  hasAccess: boolean;
  rejectScan: () => void;
  restoreScan: () => void;
}

type modalProps = OrderModalActionsProps & OrderModalCommonActionsProps;

export const OrderModalActions = (props: modalProps) => {
  const t = useT();
  const ACTION_STRINGS = {
    SubmitOrder: t("component.orders.submitOrder", "Submit Order"),
    SubmitQuote: t("component.orders.submitQuote", "Submit for Quote"),
    Next: t("component.orders.next", "Next"),
    Cancel: t("component.common.cancel", "Cancel"),
    Prev: t("component.orders.prev", "Prev"),
    Done: t("component.orders.done", "Done"),
  };
  const isLastStep = props.finalStep === props.currentStep || props.isLastStep;
  const currentStep = props.currentStep;
  const isSubmitted = props.currentOrder?.status === OrderStatus.Ordered;
  const isInReview =
    props.currentOrder?.status === OrderStatus.QuoteInReview ||
    props.currentOrder?.status === OrderStatus.OrderInReview;
  const isQuoted =
    props.currentOrder?.status === OrderStatus.QuotedOptions ||
    props.currentOrder?.status === OrderStatus.ReadyForOrder;
  const { user } = useAuth();
  const { state } = React.useContext(OrderContext);
  const getButtonText = () => {
    if (isLastStep) {
      return isQuoted ? ACTION_STRINGS.SubmitOrder : ACTION_STRINGS.SubmitQuote;
    }
    return ACTION_STRINGS.Next;
  };

  const checkIfSubmissionIsProhibited = (text: string): boolean => {
    if (text === ACTION_STRINGS.SubmitQuote || text === ACTION_STRINGS.SubmitOrder) {
      return !checkFeatureAuthorization(Features.SaveProject, user?.role);
    }
    return false;
  };

  const isRejected = state.order?.lathamSpec?.isScanRejected;

  return (
    <div className={"controls"}>
      <Button
        basic
        className={"control-left"}
        onClick={props.cancelOrder}
        content={ACTION_STRINGS.Cancel}
      />
      <>
        {state.order?.status === OrderStatus.QuoteInReview && !isRejected && (
          <RejectScanButton onSubmit={props.rejectScan} />
        )}
      </>
      <>
        {/* if it is a latham admin or customer service, we need to be able to undo the rejection */}
        {isRejected &&
          (user?.role === Role.CUST_SERV ||
            user?.role === Role.LATHAM_ADMIN ||
            user?.role === Role.BAI_ADMIN) && (
            <RestoreScanButton onSubmit={props.restoreScan} />
          )}
      </>
      {currentStep > 0 && !isSubmitted ? (
        <Button
          className={"control-right"}
          primary
          onClick={() => {
            props.previousStep();
          }}
          content={ACTION_STRINGS.Prev}
        />
      ) : null}
      {isSubmitted ? (
        <Button
          className={"control-right"}
          primary
          onClick={props.cancelOrder}
          content={ACTION_STRINGS.Done}
        />
      ) : isInReview && isLastStep && !props.hasAccess ? null : (
        <Button
          data-testid={`order-modal-actions-button-${currentStep}`}
          className={currentStep <= props.initialStep ? "control-right" : ""}
          primary
          disabled={
            props.nextDisabled ||
            (props.archived && getButtonText() !== ACTION_STRINGS.Next) ||
            checkIfSubmissionIsProhibited(getButtonText())
          }
          onClick={() => {
            isLastStep ? props.submitOrder() : props.nextStep();
          }}
          content={getButtonText()}
        />
      )}
    </div>
  );
};
